import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQuery, type UseQueryResult } from '@tanstack/react-query';
import flatten from 'lodash/flatten';

import { RecurringSubscriptionData, RecurringSubscriptionListQuery } from 'chaika-frontend-types/frontend-cms';

import { recurringSubscriptionQueryOptions } from '@store/RecurringSubscription/queries/recurringSubscriptionQueries';

export function useRecurringSubscriptionById(id: string): UseQueryResult<RecurringSubscriptionData> {
    return useQuery(recurringSubscriptionQueryOptions.byId(id));
}

interface UseRecurringSubscriptionsOptions {
    staleTime?: number;
    enabled?: boolean;
}

export function useRecurringSubscriptions(
    filters: RecurringSubscriptionListQuery = {},
    { staleTime, enabled }: UseRecurringSubscriptionsOptions = {},
): UseQueryResult<RecurringSubscriptionData[]> {
    return useQuery({ ...recurringSubscriptionQueryOptions.list(filters), staleTime, enabled });
}

interface RecurringSubscriptionListResult {
    recurringSubscriptions: RecurringSubscriptionData[];
    count: number;
    hasNextPage: boolean;
    isFetching: boolean;
    fetchNextPage(): void;
}

export function useRecurringSubscriptionList(
    filters: RecurringSubscriptionListQuery = {},
    { enabled }: { enabled?: boolean } = {},
): RecurringSubscriptionListResult {
    const { data, hasNextPage, isFetching, fetchNextPage } = useInfiniteQuery({
        ...recurringSubscriptionQueryOptions.paginatedList(filters),
        enabled,
    });

    const recurringSubscriptions = useMemo(
        () => (data ? flatten(data.pages.map(page => page.recurringSubscriptions)) : []),
        [data],
    );
    const count = data ? (data.pages[data.pages.length - 1]?.count ?? 0) : 0;

    const loadMore = useCallback(() => {
        fetchNextPage();
    }, [fetchNextPage]);

    return {
        recurringSubscriptions,
        count,
        hasNextPage,
        isFetching,
        fetchNextPage: loadMore,
    };
}
